import React, { useEffect, useState } from "react";
import { Archive, Building2, Eye, FileDown, FileText, Loader2, Plus, RefreshCw, RotateCcw, Search, ShieldCheck, Trash2, Users, X } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../api";
import { fmtDate, useLang } from "../i18n";
import DateInput from "../components/DateInput";
import { ContactInbox } from "../components/ContactInbox";
import { PlatformUserActions } from "../components/PlatformUserActions";
import { PlatformUsers } from "../components/PlatformUsers";

const EMPTY = { tenant_name: "", name: "", email: "", password: "", ruc: "", phone: "", contact_person: "" };

const MODULE_LABELS = { sanctions: "nav.sanctions", pep: "nav.pep", blacklist: "nav.blacklist", dd_natural: "nav.dd_natural",
  dd_juridica: "nav.dd_juridica", declaracion: "nav.declaracion", certificacion: "nav.certificacion", carta: "nav.carta",
  dd_aseguradora: "nav.dd_aseguradora", pj_aseguradora: "nav.pj_aseguradora", risk: "nav.risk", employees: "nav.employees",
  suppliers: "nav.suppliers", ros: "nav.ros", rte: "nav.rte", freeze: "nav.freeze", manual: "nav.manual",
  history: "nav.history", pep_panama: "nav.pep_panama", fbi: "nav.fbi", interpol: "nav.interpol", wb: "nav.wb", osint: "nav.osint",
  namus: "nav.namus", icmp: "nav.icmp", cyber: "nav.cyber", ntas: "nav.ntas", vessels: "nav.vessels" };
const SOURCE_MODS = ["fbi", "interpol", "wb", "osint", "namus", "icmp", "cyber", "ntas", "vessels"];

export default function PlatformAdmin() {
  const { t } = useLang();
  const [stats, setStats] = useState(null);
  const [tenants, setTenants] = useState(null);
  const [usage, setUsage] = useState([]);
  const [rejectFor, setRejectFor] = useState(null);
  const [reason, setReason] = useState("");
  const [detail, setDetail] = useState(null);
  const [dform, setDform] = useState({ name: "", ruc: "", phone: "", contact_person: "" });
  const [ov, setOv] = useState(null);
  const [ovTab, setOvTab] = useState("info");
  const [modSel, setModSel] = useState([]);
  const [modList, setModList] = useState([]);
  const [purge, setPurge] = useState(null);
  const [purgeSel, setPurgeSel] = useState([]);
  const [trash, setTrash] = useState(null);
  const [lookupQ, setLookupQ] = useState("");
  const [lookup, setLookup] = useState(null);
  const [lookupBusy, setLookupBusy] = useState(false);
  const [sync, setSync] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const loadSync = () => api.get("/platform/sync-status").then((r) => setSync(r.data)).catch(() => {});
  useEffect(() => { loadSync(); }, []);
  const runSync = async (sources) => {
    setSyncing(true);
    try { await api.post("/platform/sync-sources", { sources }); toast.success(t("pf.sync_started")); setTimeout(loadSync, 15000); } catch (e) { toast.error(apiError(e)); } finally { setSyncing(false); }
  };
  const runLookup = async (e) => {
    e?.preventDefault();
    if (lookupQ.trim().length < 3) return;
    setLookupBusy(true);
    try { const { data } = await api.get(`/platform/lookup?q=${encodeURIComponent(lookupQ.trim())}`); setLookup(data); } catch (err) { toast.error(apiError(err)); } finally { setLookupBusy(false); }
  };
  const openTrash = async () => { try { const { data } = await api.get("/platform/trash"); setTrash(data); } catch (e) { toast.error(apiError(e)); } };
  const restore = async (x) => { try { await api.post(`/platform/trash/${x.id}/restore`); toast.success(t("pf.trash_restored").replace("{name}", x.name)); load(); } catch (e) { toast.error(apiError(e)); } };
  const hardDelete = async (x) => {
    if (!window.confirm(t("pf.trash_delete_ask").replace("{name}", x.name))) return;
    try { await api.delete(`/platform/trash/${x.id}`); toast.success(t("pf.trash_deleted").replace("{name}", x.name)); openTrash(); } catch (e) { toast.error(apiError(e)); }
  };
  const openPurge = async () => {
    try { const { data } = await api.get("/platform/test-tenants"); setPurge(data); setPurgeSel(data.map((x) => x.id)); }
    catch (e) { toast.error(apiError(e)); }
  };
  const doPurge = async () => {
    if (!window.confirm(t("pf.purge_ask").replace("{n}", purgeSel.length))) return;
    setBusy(true);
    try { const { data } = await api.post("/platform/test-tenants/purge", { tenant_ids: purgeSel }); toast.success(t("pf.purge_done").replace("{n}", data.deleted.length)); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const [accesses, setAccesses] = useState([]);
  const [accMode, setAccMode] = useState("all");
  const [accStart, setAccStart] = useState("");
  const [accEnd, setAccEnd] = useState("");
  const [accBusy, setAccBusy] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [kpiModal, setKpiModal] = useState(null);
  const [kpiRows, setKpiRows] = useState([]);
  const [kpiLoading, setKpiLoading] = useState(false);

  const KPI_ENDPOINTS = { users: "/platform/users", searches: "/platform/searches", forms: "/platform/forms" };
  const KPI_TITLES = { tenants: t("pf.stats_tenants"), users: t("pf.stats_users"), searches: t("pf.stats_searches"), forms: t("pf.stats_forms") };
  const KPI_COLUMNS = {
    tenants: [
      { key: "name", label: t("pf.tenants") }, { key: "ruc", label: "RUC" },
      { key: "users_count", label: t("pf.users") }, { key: "searches_count", label: t("pf.searches") },
      { key: "forms_count", label: t("pf.forms") }, { key: "created_at", label: t("ah.date"), date: true },
    ],
    users: [
      { key: "tenant_name", label: t("ah.tenant") }, { key: "count", label: t("pf.users") },
    ],
    searches: [
      { key: "tenant_name", label: t("ah.tenant") }, { key: "listas", label: t("pf.searches") },
      { key: "pep", label: "PEP/RCA" }, { key: "total", label: "Total" },
    ],
    forms: [
      { key: "tenant_name", label: t("ah.tenant") }, { key: "count", label: t("pf.forms") },
    ],
  };

  const openKpi = async (type) => {
    setKpiModal(type);
    if (type === "tenants") {
      setKpiRows(tenants || []);
      return;
    }
    setKpiLoading(true);
    try {
      const { data } = await api.get(KPI_ENDPOINTS[type]);
      setKpiRows(data);
    } catch (err) {
      toast.error(apiError(err));
      setKpiRows([]);
    } finally {
      setKpiLoading(false);
    }
  };

  const [accQueried, setAccQueried] = useState(false);
  const [accLoading, setAccLoading] = useState(false);
  const [accUser, setAccUser] = useState("");
  const [accOrg, setAccOrg] = useState("");
  const [accType, setAccType] = useState("all");
  const loadAccesses = (start = accStart, end = accEnd) => {
    const p = new URLSearchParams();
    if (start) p.set("start", start);
    if (end) p.set("end", end);
    const qs = p.toString();
    setAccLoading(true); setAccQueried(true);
    api.get(`/platform/access-history${qs ? `?${qs}` : ""}`).then((r) => setAccesses(r.data)).catch(() => setAccesses([])).finally(() => setAccLoading(false));
  };
  const accFiltered = accesses.filter((e) =>
    (!accUser || `${e.name || ""} ${e.email || ""}`.toLowerCase().includes(accUser.toLowerCase())) &&
    (!accOrg || (e.tenant_name || "").toLowerCase().includes(accOrg.toLowerCase())) &&
    (accType === "all" || (accType === "action" ? !!e.action : !e.action)));
  const EvBadge = ({ e }) => (
    <span className={`inline-block text-[10px] font-bold px-1.5 py-0.5 rounded ${e.action ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`}>{e.action ? t("ah.type_action") : t("ah.type_login")}</span>
  );

  const applyRange = (s, e) => { setAccStart(s); setAccEnd(e); loadAccesses(s, e); };
  const onAccMode = (mode) => {
    setAccMode(mode);
    const today = new Date().toISOString().slice(0, 10);
    if (mode === "dia") applyRange(today, today);
    else if (mode === "semana") onAccWeek(today);
    else if (mode === "mes") onAccMonth(today.slice(0, 7));
    else applyRange("", "");
  };
  const onAccDay = (v) => applyRange(v, v);
  const onAccWeek = (v) => {
    if (!v) return applyRange("", "");
    const d = new Date(`${v}T12:00:00Z`);
    d.setUTCDate(d.getUTCDate() + 6);
    applyRange(v, d.toISOString().slice(0, 10));
  };
  const onAccMonth = (v) => {
    if (!v) return applyRange("", "");
    const [y, m] = v.split("-").map(Number);
    const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
    applyRange(`${v}-01`, `${v}-${String(last).padStart(2, "0")}`);
  };

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };
  const pdfEntry = async (ev) => {
    try {
      const res = await api.post("/platform/access-history/pdf/entry", { entry: ev }, { responseType: "blob" });
      downloadBlob(res.data, `acceso-${ev.email || "registro"}.pdf`);
    } catch (err) {
      toast.error(apiError(err));
    }
  };
  const pdfAll = async () => {
    setAccBusy(true);
    try {
      const p = new URLSearchParams();
      if (accStart) p.set("start", accStart);
      if (accEnd) p.set("end", accEnd);
      const res = await api.get(`/platform/access-history/pdf?${p}`, { responseType: "blob" });
      downloadBlob(res.data, "historial-accesos.pdf");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setAccBusy(false);
    }
  };

  const load = () => {
    setPurge(null); setTrash(null);
    api.get("/platform/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/platform/tenants").then((r) => setTenants(r.data)).catch((e) => { toast.error(apiError(e)); setTenants([]); });
    api.get("/platform/usage").then((r) => setUsage(r.data)).catch(() => setUsage([]));
    api.get("/platform/modules").then((r) => setModList(r.data.modules || [])).catch(() => setModList([]));
    if (accQueried) loadAccesses();
  };
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/platform/tenants", form);
      toast.success(t("pf.tenant_created"));
      setShowForm(false);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (tnt) => {
    try {
      await api.patch(`/platform/tenants/${tnt.id}`, { active: !(tnt.active !== false) });
      toast.success(t("u.updated"));
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const approve = async (tnt) => {
    try {
      await api.post(`/platform/tenants/${tnt.id}/approve`);
      toast.success(t("pf.approved_msg"));
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const reject = async () => {
    setBusy(true);
    try {
      await api.post(`/platform/tenants/${rejectFor.id}/reject`, { reason });
      toast.success(t("pf.rejected_msg"));
      setRejectFor(null);
      setReason("");
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const openDetail = (tnt) => {
    setDetail(tnt);
    setDform({ name: tnt.name || "", ruc: tnt.ruc || "", phone: tnt.phone || "", contact_person: tnt.contact_person || "" });
    const all = modList.length ? modList : Object.keys(MODULE_LABELS);
    setModSel(tnt.allowed_modules == null ? all : tnt.allowed_modules);
    setOvTab("info");
    setOv(null);
    api.get(`/platform/tenants/${tnt.id}/overview`).then((r) => setOv(r.data)).catch(() => setOv({ users: [], queries: [], forms: [], accesses: [] }));
  };

  const OV_TABS = [["info", "pf.tab_info"], ["users", "pf.tab_users"], ["queries", "pf.tab_queries"], ["forms", "pf.tab_forms"], ["accesses", "pf.tab_accesses"]];
  const OV_COLS = {
    users: [{ key: "name", label: t("u.name") }, { key: "email", label: "Email" }, { key: "role", label: t("pf.col_role") }, { key: "active", label: t("u.status") }, { key: "last_login_at", label: t("pf.last_login"), date: true }],
    queries: [{ key: "consult_number", label: t("pf.col_number") }, { key: "query", label: t("pf.col_query") }, { key: "kind", label: t("pf.col_type") }, { key: "result", label: t("pf.col_result") }, { key: "created_at", label: t("ah.date"), date: true }],
    forms: [{ key: "form_number", label: t("pf.col_form") }, { key: "title", label: t("pf.col_title") }, { key: "form_type", label: t("pf.col_type") }, { key: "created_at", label: t("ah.date"), date: true }],
    accesses: [{ key: "email", label: "Email" }, { key: "at", label: t("ah.date"), date: true }, { key: "ip", label: "IP" }, { key: "device", label: t("ah.device") }],
  };

  const reassignForm = async (formId, tenantId) => {
    try {
      await api.patch(`/forms/${formId}/assign`, { tenant_id: tenantId });
      toast.success(t("pf.reassigned"));
      if (detail) {
        const { data } = await api.get(`/platform/tenants/${detail.id}/overview`);
        setOv(data);
      }
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const saveDetail = async () => {
    setBusy(true);
    try {
      await api.patch(`/platform/tenants/${detail.id}`, { ...dform, allowed_modules: modSel });
      toast.success(t("u.updated"));
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const approveFromModal = async () => {
    setBusy(true);
    try {
      await api.patch(`/platform/tenants/${detail.id}`, { ...dform, allowed_modules: modSel });
      await api.post(`/platform/tenants/${detail.id}/approve`);
      toast.success(t("pf.approved_msg"));
      setDetail(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const pendingTenants = (tenants || []).filter((x) => x.status === "pending");

  const cards = stats ? [
    { label: t("pf.stats_tenants"), value: `${stats.tenants_active}/${stats.tenants}`, icon: Building2, testid: "pf-kpi-tenants", type: "tenants" },
    { label: t("pf.stats_users"), value: stats.users, icon: Users, testid: "pf-kpi-users", type: "users" },
    { label: t("pf.stats_searches"), value: stats.searches + stats.pep_searches, icon: ShieldCheck, testid: "pf-kpi-searches", type: "searches" },
    { label: t("pf.stats_forms"), value: stats.forms, icon: FileText, testid: "pf-kpi-forms", type: "forms" },
  ] : [];

  return (
    <div className="space-y-6" data-testid="platform-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">{t("pf.title")}</h1>
          <p className="text-sm text-slate-500 mt-1">{t("pf.subtitle")}</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={openTrash} data-testid="trash-button" className="flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm font-bold hover:bg-slate-50 transition-colors">
            <Archive className="w-4 h-4" /> {t("pf.trash_btn")}
          </button>
          <button onClick={openPurge} data-testid="purge-test-tenants-button" className="flex items-center gap-2 px-4 py-2 rounded-lg border border-red-300 text-red-700 text-sm font-bold hover:bg-red-50 transition-colors">
            <Trash2 className="w-4 h-4" /> {t("pf.purge_btn")}
          </button>
          <button onClick={() => setShowForm(!showForm)} data-testid="new-tenant-button" className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#044CBC] text-white text-sm font-bold hover:bg-[#033A8C] transition-colors">
            <Plus className="w-4 h-4" /> {t("pf.new_tenant")}
          </button>
        </div>
      </div>

      <div className="ec-card p-4 space-y-3" data-testid="sync-box">
        <div className="flex flex-wrap items-center gap-3">
          <div className="text-sm font-bold text-slate-800 flex items-center gap-2"><RefreshCw className="w-4 h-4 text-[#044CBC]" /> {t("pf.sync_title")}</div>
          <button onClick={loadSync} className="text-xs font-bold text-[#044CBC] underline" data-testid="sync-refresh">{t("pf.sync_refresh")}</button>
          <div className="ml-auto flex gap-2">
            {["WB", "NAMUS"].map((s) => <button key={s} onClick={() => runSync([s])} disabled={syncing} className="px-3 py-1.5 rounded-lg border border-[#16335F] text-[#16335F] text-xs font-bold hover:bg-[#EFF4FC] disabled:opacity-50" data-testid={`sync-${s.toLowerCase()}-btn`}>{t("pf.sync_now")} {s === "WB" ? "Banco Mundial" : "NamUs"}</button>)}
          </div>
        </div>
        {sync && (
          <div className="grid sm:grid-cols-3 gap-2 text-xs" data-testid="sync-status">
            {[["WB", "Banco Mundial", sync.wb_debarments, "wb_debarments"], ["NAMUS", "NamUs 2.0", sync.namus_cases, "namus_cases"], ["PEP_PANAMA", "Lista PEP Panamá", sync.pep_panama, "pep_panama"]].map(([k, label, n, col]) => {
              const st = sync.status?.[k] || {};
              return <div key={k} className="rounded-lg border border-slate-200 px-3 py-2" data-testid={`sync-card-${k.toLowerCase()}`}>
                <div className="font-bold text-slate-800">{label}</div>
                <div className="text-slate-600">{(n || 0).toLocaleString()} {t("pf.sync_records")} · <span className="font-mono">{col}</span></div>
                {k !== "PEP_PANAMA" && <div className={st.status === "ok" ? "text-emerald-700" : st.status === "error" ? "text-red-700" : "text-slate-400"}>{st.status || "—"} · {st.last_update ? fmtDate(st.last_update) : "—"}{st.error ? ` · ${st.error}` : ""}</div>}
              </div>;
            })}
          </div>
        )}
      </div>

      <PlatformUsers tenants={tenants} />
      <ContactInbox />

      <div className="ec-card p-4 space-y-3" data-testid="lookup-box">
        <div className="text-sm font-bold text-slate-800 flex items-center gap-2"><Search className="w-4 h-4 text-[#044CBC]" /> {t("pf.lookup_title")}</div>
        <p className="text-xs text-slate-500">{t("pf.lookup_help")}</p>
        <form onSubmit={runLookup} className="flex flex-wrap gap-2">
          <input className="ec-input flex-1 min-w-[240px]" value={lookupQ} onChange={(e) => setLookupQ(e.target.value)} placeholder={t("pf.lookup_ph")} data-testid="lookup-input" />
          <button type="submit" disabled={lookupBusy || lookupQ.trim().length < 3} className="px-4 py-2 rounded-lg bg-[#16335F] text-white text-sm font-bold disabled:opacity-50" data-testid="lookup-btn">{lookupBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : t("common.search")}</button>
        </form>
        {lookup && (
          <div className="grid lg:grid-cols-3 gap-3 text-xs" data-testid="lookup-results">
            {lookup.tenants.length + lookup.users.length + lookup.events.length === 0 && <div className="text-slate-500" data-testid="lookup-none">{t("pf.lookup_none")}</div>}
            {lookup.tenants.length > 0 && <div className="space-y-1.5"><div className="font-bold text-slate-700 uppercase tracking-wide">{t("pf.lookup_tenants")}</div>
              {lookup.tenants.map((x) => <div key={x.id} className="rounded-lg border border-slate-200 px-3 py-2" data-testid={`lookup-tenant-${x.id}`}>
                <div className="font-semibold text-slate-800">{x.name}</div>
                <div className="text-slate-500">Estado: <b>{x.status || (x.active ? "approved" : "inactive")}</b> · {x.active ? t("common.activate").replace("Activar", "Activa").replace("Activate", "Active") : t("common.inactive")} · {x.users_count} {t("pf.users_short")} · {fmtDate(x.created_at)}</div>
                <div className={x.in_trash ? "text-red-700 font-bold" : x.in_selector ? "text-emerald-700" : "text-amber-700"}>{x.in_trash ? `${t("pf.lookup_in_trash")} (${fmtDate(x.trashed_at)})` : x.in_selector ? t("pf.lookup_in_selector") : `${t("pf.lookup_not_selector")}: ${x.status}`}</div>
                {x.in_trash && <button onClick={() => restore(x).then(() => runLookup())} className="mt-1 px-3 py-1 rounded bg-[#044CBC] text-white font-bold" data-testid={`lookup-restore-${x.id}`}>{t("pf.trash_restore")}</button>}
              </div>)}</div>}
            {lookup.users.length > 0 && <div className="space-y-1.5"><div className="font-bold text-slate-700 uppercase tracking-wide">{t("pf.lookup_users")}</div>
              {lookup.users.map((u) => <div key={u.id} className="rounded-lg border border-slate-200 px-3 py-2" data-testid={`lookup-user-${u.id}`}>
                <div className="font-semibold text-slate-800">{u.name} <span className="font-normal text-slate-500">({u.role})</span></div>
                <div className="text-slate-600 break-all">{u.email}</div>
                <div className="text-slate-500">{u.tenant_name || "—"} · {u.active ? "activo" : t("common.inactive")}{u.trashed ? " · papelera" : ""}{u.archived_at ? ` · archivado ${fmtDate(u.archived_at)}` : ""}{u.expires_at ? ` · vence ${fmtDate(u.expires_at)}` : ""}</div>
              </div>)}</div>}
            {lookup.events.length > 0 && <div className="space-y-1.5"><div className="font-bold text-slate-700 uppercase tracking-wide">{t("pf.lookup_events")}</div>
              {lookup.events.map((ev, i) => <div key={i} className="rounded-lg border border-slate-200 px-3 py-2" data-testid={`lookup-event-${i}`}>
                <div className="text-slate-800">{ev.action || "login"}</div><div className="text-slate-500">{ev.email} · {ev.acting_tenant || "—"} · {fmtDate(ev.at)} · {ev.ip}</div>
              </div>)}</div>}
          </div>
        )}
      </div>

      {trash && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setTrash(null)} data-testid="trash-modal">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading text-lg font-extrabold text-slate-900 flex items-center gap-2"><Archive className="w-5 h-5 text-[#044CBC]" /> {t("pf.trash_title")}</h3>
            <p className="text-xs text-slate-500">{t("pf.trash_help")}</p>
            {trash.length === 0 ? <p className="text-sm text-slate-500" data-testid="trash-empty">{t("pf.trash_none")}</p> : (
              <ul className="space-y-1.5 max-h-80 overflow-y-auto">
                {trash.map((x) => (
                  <li key={x.id} className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid={`trash-row-${x.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-slate-800 truncate">{x.name}</div>
                      <div className="text-xs text-slate-500">{x.users} {t("pf.users_short")} · {t("pf.trash_since")} {fmtDate(x.trashed_at)} · <span className={x.days_left <= 5 ? "text-red-600 font-bold" : "font-semibold"} data-testid={`trash-days-${x.id}`}>{x.days_left} {t("pf.trash_days_left")}</span></div>
                    </div>
                    <button onClick={() => restore(x)} data-testid={`trash-restore-${x.id}`} className="px-3 py-1.5 rounded-lg bg-[#044CBC] text-white text-xs font-bold hover:bg-[#033A8C] inline-flex items-center gap-1"><RotateCcw className="w-3.5 h-3.5" /> {t("pf.trash_restore")}</button>
                    <button onClick={() => hardDelete(x)} data-testid={`trash-delete-${x.id}`} className="px-3 py-1.5 rounded-lg border border-red-300 text-red-700 text-xs font-bold hover:bg-red-50 inline-flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" /> {t("pf.trash_delete_now")}</button>
                  </li>
                ))}
              </ul>
            )}
            <div className="flex justify-end"><button onClick={() => setTrash(null)} className="px-4 py-2 rounded-lg border border-slate-300 text-sm font-bold text-slate-700" data-testid="trash-close">{t("common.close")}</button></div>
          </div>
        </div>
      )}

      {purge && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setPurge(null)} data-testid="purge-modal">
          <div className="bg-white rounded-xl shadow-xl max-w-xl w-full p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading text-lg font-extrabold text-slate-900 flex items-center gap-2"><Trash2 className="w-5 h-5 text-red-600" /> {t("pf.purge_title")}</h3>
            <p className="text-xs text-slate-500">{t("pf.purge_help")}</p>
            {purge.length === 0 ? <p className="text-sm text-slate-500" data-testid="purge-empty">{t("pf.purge_none")}</p> : (
              <ul className="space-y-1.5 max-h-72 overflow-y-auto">
                {purge.map((x) => (
                  <li key={x.id} className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid={`purge-row-${x.id}`}>
                    <input type="checkbox" checked={purgeSel.includes(x.id)} onChange={(e) => setPurgeSel(e.target.checked ? [...purgeSel, x.id] : purgeSel.filter((i) => i !== x.id))} data-testid={`purge-check-${x.id}`} />
                    <span className="font-semibold text-slate-800 flex-1 truncate">{x.name}</span>
                    <span className="text-xs text-slate-500">{x.admin_email} · {x.users} {t("pf.users_short")}</span>
                  </li>
                ))}
              </ul>
            )}
            <div className="flex justify-end gap-2">
              <button onClick={() => setPurge(null)} className="px-4 py-2 rounded-lg border border-slate-300 text-sm font-bold text-slate-700" data-testid="purge-cancel">{t("common.cancel")}</button>
              <button onClick={doPurge} disabled={!purgeSel.length || busy} className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-bold hover:bg-red-700 disabled:opacity-50 flex items-center gap-2" data-testid="purge-confirm">
                {busy && <Loader2 className="w-4 h-4 animate-spin" />} {t("pf.purge_confirm")} ({purgeSel.length})
              </button>
            </div>
          </div>
        </div>
      )}

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {cards.map((c) => (
            <div key={c.testid} className="ec-card p-5 cursor-pointer hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200" data-testid={c.testid} onClick={() => openKpi(c.type)} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && openKpi(c.type)}>
              <div className="w-9 h-9 rounded-lg bg-orange-50 text-[#044CBC] flex items-center justify-center mb-3"><c.icon className="w-5 h-5" /></div>
              <div className="font-heading text-2xl font-extrabold text-slate-900">{c.value}</div>
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mt-1">{c.label}</div>
            </div>
          ))}
        </div>
      )}

      {usage.length > 0 && (
        <div className="ec-card p-6" data-testid="usage-chart">
          <h3 className="font-heading font-bold text-slate-900 mb-1">{t("pf.usage_title")}</h3>
          {(() => {
            const PALETTE = ["#044CBC", "#16335F", "#033A8C", "#E9B949", "#64748B", "#059669", "#0284C7"];
            const tenantNames = [...new Set(usage.map((u) => u.tenant))];
            const colorOf = (name) => PALETTE[tenantNames.indexOf(name) % PALETTE.length];
            const months = [...new Set(usage.map((u) => u.month))];
            const totals = months.map((m) => usage.filter((u) => u.month === m).reduce((s, u) => s + u.count, 0));
            const max = Math.max(...totals, 1);
            return (
              <>
                <div className="flex flex-wrap gap-3 my-3">
                  {tenantNames.map((n) => (
                    <span key={n} className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-600">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: colorOf(n) }} /> {n}
                    </span>
                  ))}
                </div>
                <div className="space-y-2.5">
                  {months.map((m, mi) => (
                    <div key={m} className="flex items-center gap-3" data-testid={`usage-row-${m}`}>
                      <span className="text-xs font-mono text-slate-500 w-16 shrink-0">{m}</span>
                      <div className="flex-1 h-6 rounded-md bg-slate-100 overflow-hidden flex">
                        {usage.filter((u) => u.month === m).map((u) => (
                          <div key={u.tenant} title={`${u.tenant}: ${u.count}`} className="h-full transition-all duration-500"
                               style={{ width: `${(u.count / max) * 100}%`, backgroundColor: colorOf(u.tenant) }} />
                        ))}
                      </div>
                      <span className="text-xs font-bold text-slate-700 w-10 text-right shrink-0">{totals[mi]}</span>
                    </div>
                  ))}
                </div>
              </>
            );
          })()}
        </div>
      )}

      {showForm && (
        <form onSubmit={submit} className="ec-card p-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4 items-end" data-testid="new-tenant-form">
          <div><label className="ec-label">{t("auth.company")}</label><input className="ec-input" value={form.tenant_name} onChange={set("tenant_name")} required data-testid="new-tenant-name" /></div>
          <div><label className="ec-label">{t("u.name")}</label><input className="ec-input" value={form.name} onChange={set("name")} required data-testid="new-tenant-admin-name" /></div>
          <div><label className="ec-label">{t("auth.email")}</label><input className="ec-input" type="email" value={form.email} onChange={set("email")} required data-testid="new-tenant-email" /></div>
          <div><label className="ec-label">{t("auth.password")}</label><input className="ec-input" type="password" value={form.password} onChange={set("password")} required minLength={8} data-testid="new-tenant-password" /></div>
          <div><label className="ec-label">{t("auth.ruc")}</label><input className="ec-input" value={form.ruc} onChange={set("ruc")} data-testid="new-tenant-ruc" /></div>
          <div><label className="ec-label">{t("auth.phone")}</label><input className="ec-input" value={form.phone} onChange={set("phone")} data-testid="new-tenant-phone" /></div>
          <div className="sm:col-span-2 lg:col-span-3">
            <button type="submit" disabled={busy} data-testid="new-tenant-submit" className="px-5 py-2.5 rounded-lg bg-[#16335F] text-white text-sm font-bold hover:bg-[#033A8C] disabled:opacity-60 flex items-center gap-2">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} {t("u.create")}
            </button>
          </div>
        </form>
      )}

      {pendingTenants.length > 0 && (
        <div className="ec-card p-6 border-l-4 border-amber-400" data-testid="pending-tenants-card">
          <h3 className="font-heading font-bold text-slate-900 mb-1">{t("pf.pending")} ({pendingTenants.length})</h3>
          <p className="text-xs text-slate-500 mb-4">{t("pf.pending_help")}</p>
          <ul className="space-y-3">
            {pendingTenants.map((tnt) => (
              <li key={tnt.id} className="bg-amber-50/60 border border-amber-100 rounded-xl p-4" data-testid={`pending-row-${tnt.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-heading font-extrabold text-slate-900" data-testid={`pending-name-${tnt.id}`}>{tnt.name}</div>
                    <div className="text-[11px] text-slate-500">{t("pf.requested_at")} {fmtDate(tnt.created_at)} · {t("pf.col_number")} {tnt.reg_number || "—"}</div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button onClick={() => openDetail(tnt)} data-testid={`view-${tnt.id}`} className="flex items-center gap-1 px-4 py-2 rounded-lg border border-[#044CBC] text-[#044CBC] text-xs font-bold hover:bg-blue-50">
                      <Eye className="w-3.5 h-3.5" /> {t("pf.view_edit")}
                    </button>
                    <button onClick={() => approve(tnt)} disabled={busy} data-testid={`approve-${tnt.id}`} className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-xs font-bold hover:bg-emerald-700 disabled:opacity-60">
                      {t("pf.approve")}
                    </button>
                    <button onClick={() => { setRejectFor(tnt); setReason(""); }} data-testid={`reject-${tnt.id}`} className="px-4 py-2 rounded-lg bg-red-600 text-white text-xs font-bold hover:bg-red-700">
                      {t("pf.reject")}
                    </button>
                  </div>
                </div>
                <dl className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2 text-sm" data-testid={`pending-details-${tnt.id}`}>
                  {[["auth.ruc", tnt.ruc], ["auth.phone", tnt.phone], ["auth.name", tnt.business_type], ["auth.contact_person", tnt.contact_person], ["u.email", tnt.admin_email]].map(([k, v]) => (
                    <div key={k} className="min-w-0">
                      <dt className="text-[10px] font-bold uppercase tracking-wide text-slate-500">{t(k)}</dt>
                      <dd className="text-slate-800 font-medium break-words" data-testid={`pending-${k.split(".")[1]}-${tnt.id}`}>{v || "—"}</dd>
                    </div>
                  ))}
                </dl>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="ec-card p-6" data-testid="access-history-section">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
          <h2 className="font-heading font-bold text-slate-900 flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-[#044CBC]" /> {t("ah.all_title")}
          </h2>
          <button onClick={pdfAll} disabled={accBusy || !accQueried} data-testid="access-export-pdf" className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#044CBC] text-white text-xs font-bold hover:bg-[#033A8C] disabled:opacity-60">
            {accBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />} {t("ah.export_all")}
          </button>
        </div>
        <div className="ec-filter-bar mb-4" data-testid="access-filters">
          <div className="lg:col-span-2">
            <label className="ec-label">{t("ah.period")}</label>
            <select className="ec-input" value={accMode} onChange={(e) => onAccMode(e.target.value)} data-testid="access-period-select">
              <option value="all">{t("ah.mode_all")}</option>
              <option value="cuenta">{t("ah.mode_account")}</option>
              <option value="dia">{t("ah.mode_day")}</option>
              <option value="semana">{t("ah.mode_week")}</option>
              <option value="mes">{t("ah.mode_month")}</option>
            </select>
          </div>
          {accMode === "dia" && (
            <div className="lg:col-span-2">
              <label className="ec-label">{t("ah.date")}</label>
              <DateInput className="ec-input" value={accStart} onChange={(e) => onAccDay(e.target.value)} data-testid="access-day-input" />
            </div>
          )}
          {accMode === "semana" && (
            <div className="lg:col-span-2">
              <label className="ec-label">{t("ah.week_start")}</label>
              <DateInput className="ec-input" value={accStart} onChange={(e) => onAccWeek(e.target.value)} data-testid="access-week-input" />
            </div>
          )}
          {accMode === "mes" && (
            <div className="lg:col-span-2">
              <label className="ec-label">{t("ah.month")}</label>
              <input type="month" className="ec-input" value={accStart.slice(0, 7)} onChange={(e) => onAccMonth(e.target.value)} data-testid="access-month-input" />
            </div>
          )}
          {(accMode === "all" || accMode === "cuenta") && (
            <>
              <div className="lg:col-span-2">
                <label className="ec-label">{t("ah.from")}</label>
                <DateInput className="ec-input" value={accStart} onChange={(e) => setAccStart(e.target.value)} data-testid="access-from-input" />
              </div>
              <div className="lg:col-span-2">
                <label className="ec-label">{t("ah.to")}</label>
                <DateInput className="ec-input" value={accEnd} onChange={(e) => setAccEnd(e.target.value)} data-testid="access-to-input" />
              </div>
            </>
          )}
          <div className="lg:col-span-2">
            <label className="ec-label">{t("ah.type")}</label>
            <select className="ec-input" value={accType} onChange={(e) => setAccType(e.target.value)} data-testid="access-type-select">
              <option value="all">{t("common.all")}</option><option value="login">{t("ah.type_login")}</option><option value="action">{t("ah.type_action")}</option>
            </select>
          </div>
          <button onClick={() => loadAccesses()} disabled={accLoading} data-testid="access-query-button" className="ec-filter-btn lg:col-span-2">
            {accLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />} {t("ah.query")}
          </button>
        </div>
        {accQueried && (
          <div className="flex flex-wrap items-center gap-3 mb-3" data-testid="access-secondary-filters">
            <input className="ec-input !w-56 !h-9 text-xs" placeholder={t("ah.filter_user")} value={accUser} onChange={(e) => setAccUser(e.target.value)} data-testid="access-user-filter" />
            <input className="ec-input !w-56 !h-9 text-xs" placeholder={t("ah.filter_org")} value={accOrg} onChange={(e) => setAccOrg(e.target.value)} data-testid="access-org-filter" />
            <span className="text-xs font-semibold text-slate-500 ml-auto" data-testid="access-count">{accFiltered.length} {t("ah.records")} · {accFiltered.filter((e) => e.action).length} {t("ah.type_action").toLowerCase()}</span>
          </div>
        )}
        {!accQueried ? (
          <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-6 py-10 text-center text-sm text-slate-500" data-testid="access-not-queried">
            <ShieldCheck className="w-8 h-8 mx-auto mb-2 text-slate-300" />{t("ah.not_queried")}
          </div>
        ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200">
          <table className="w-full text-sm min-w-[720px]">
            <thead>
              <tr className="bg-[#16335F] text-white text-left">
                <th className="px-3 py-2.5 text-xs font-bold uppercase tracking-wide">{t("ah.user")}</th>
                <th className="px-3 py-2.5 text-xs font-bold uppercase tracking-wide">{t("ah.type")}</th>
                <th className="px-3 py-2.5 text-xs font-bold uppercase tracking-wide">{t("ah.tenant")}</th>
                <th className="px-3 py-2.5 text-xs font-bold uppercase tracking-wide">{t("ah.date")}</th>
                <th className="px-3 py-2.5 text-xs font-bold uppercase tracking-wide">IP</th>
                <th className="px-3 py-2.5 text-xs font-bold uppercase tracking-wide">{t("ah.device")}</th>
                <th className="px-3 py-2.5 text-xs font-bold uppercase tracking-wide text-right">{t("ah.export_pdf")}</th>
              </tr>
            </thead>
            <tbody>
              {accLoading ? (
                <tr><td colSpan={7} className="px-3 py-8 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />{t("common.loading")}</td></tr>
              ) : accFiltered.length === 0 ? (
                <tr><td colSpan={7} className="px-3 py-8 text-center text-slate-400" data-testid="platform-access-empty">{t("ah.empty")}</td></tr>
              ) : accMode === "cuenta" ? (
                Object.entries(accFiltered.reduce((acc, ev) => { const k = ev.email || "—"; (acc[k] = acc[k] || []).push(ev); return acc; }, {})).map(([email, items]) => (
                  <React.Fragment key={email}>
                    <tr className="bg-slate-100 border-t border-slate-200" data-testid={`access-group-${email}`}>
                      <td colSpan={7} className="px-3 py-2 text-xs font-bold text-slate-700">
                        {items[0].name || "—"} · {email} <span className="text-slate-400 font-semibold">({items.length} {t("ah.accesses")})</span>
                      </td>
                    </tr>
                    {items.map((e, i) => (
                      <tr key={`${email}-${i}`} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`platform-access-row-${email}-${i}`}>
                        <td className="px-3 py-2.5 text-xs text-slate-700"><span className="font-semibold">{e.name || "—"}</span><div className="text-slate-400">{e.email}</div></td>
                        <td className="px-3 py-2.5"><EvBadge e={e} /></td>
                        <td className="px-3 py-2.5 text-xs text-slate-600">{e.tenant_name || "—"}{e.action && <div className="text-amber-700 font-semibold" data-testid={`access-action-${email}-${i}`}>{e.action}</div>}</td>
                        <td className="px-3 py-2.5 font-mono text-xs text-slate-700">{fmtDate(e.at)}</td>
                        <td className="px-3 py-2.5 font-mono text-xs text-slate-600">{e.ip || "—"}</td>
                        <td className="px-3 py-2.5 text-xs text-slate-600">{e.device || "—"}</td>
                        <td className="px-3 py-2.5 text-right">
                          <button onClick={() => pdfEntry(e)} title={t("ah.export_pdf")} data-testid={`access-pdf-${email}-${i}`} className="text-[#044CBC] hover:text-[#033A8C]"><FileDown className="w-4 h-4" /></button>
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))
              ) : accFiltered.map((e, i) => (
                <tr key={i} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`platform-access-row-${i}`}>
                  <td className="px-3 py-2.5 text-xs text-slate-700"><span className="font-semibold">{e.name || "—"}</span><div className="text-slate-400">{e.email}</div></td>
                  <td className="px-3 py-2.5"><EvBadge e={e} /></td>
                  <td className="px-3 py-2.5 text-xs text-slate-600">{e.tenant_name || "—"}{e.action && <div className="text-amber-700 font-semibold" data-testid={`access-action-${i}`}>{e.action}</div>}</td>
                  <td className="px-3 py-2.5 font-mono text-xs text-slate-700">{fmtDate(e.at)}</td>
                  <td className="px-3 py-2.5 font-mono text-xs text-slate-600">{e.ip || "—"}</td>
                  <td className="px-3 py-2.5 text-xs text-slate-600">{e.device || "—"}</td>
                  <td className="px-3 py-2.5 text-right">
                    <button onClick={() => pdfEntry(e)} title={t("ah.export_pdf")} data-testid={`access-pdf-${i}`} className="text-[#044CBC] hover:text-[#033A8C]"><FileDown className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
      </div>

      {detail && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setDetail(null)}>          <div className="ec-card max-w-4xl w-full p-6 space-y-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="tenant-detail-modal">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="font-heading font-bold text-slate-900" data-testid="tenant-detail-name">{detail.name}</h3>
                <p className="text-xs text-slate-500">{t("pf.detail_title")} · {detail.id_prefix || "—"} · {fmtDate(detail.created_at)}</p>
              </div>
              <button onClick={() => setDetail(null)} className="text-slate-400 hover:text-slate-700" data-testid="tenant-detail-close"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-2" data-testid="tenant-detail-tabs">
              {OV_TABS.map(([k, lbl]) => (
                <button key={k} onClick={() => setOvTab(k)} data-testid={`tenant-tab-${k}`} className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${ovTab === k ? "bg-[#044CBC] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{t(lbl)}</button>
              ))}
            </div>
            {ovTab === "info" && (<>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><label className="ec-label">{t("pf.tenants")}</label><input className="ec-input" value={dform.name} onChange={(e) => setDform({ ...dform, name: e.target.value })} data-testid="tenant-edit-name" /></div>
              <div><label className="ec-label">RUC</label><input className="ec-input" value={dform.ruc} onChange={(e) => setDform({ ...dform, ruc: e.target.value })} data-testid="tenant-edit-ruc" /></div>
              <div><label className="ec-label">{t("auth.phone")}</label><input className="ec-input" value={dform.phone} onChange={(e) => setDform({ ...dform, phone: e.target.value })} data-testid="tenant-edit-phone" /></div>
              <div><label className="ec-label">{t("auth.contact_person")}</label><input className="ec-input" value={dform.contact_person} onChange={(e) => setDform({ ...dform, contact_person: e.target.value })} data-testid="tenant-edit-contact" /></div>
            </div>
            <div>
              <label className="ec-label">{t("pf.modules")}</label>
              <div className="flex flex-wrap gap-2 mb-2" data-testid="plan-quick-buttons">
                {[["all", () => modList.length ? modList : Object.keys(MODULE_LABELS)],
                  ["sources", () => [...new Set([...modSel, ...SOURCE_MODS])]],
                  ["no_sources", () => modSel.filter((m) => !SOURCE_MODS.includes(m))],
                  ["basic", () => (modList.length ? modList : Object.keys(MODULE_LABELS)).filter((m) => !SOURCE_MODS.includes(m))]].map(([k, fn]) => (
                  <button key={k} type="button" onClick={() => setModSel(fn())} data-testid={`plan-quick-${k}`}
                          className="px-3 py-1.5 rounded-lg border border-[#044CBC] text-[#044CBC] text-xs font-bold hover:bg-blue-50">{t(`pf.plan_${k}`)}</button>
                ))}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="tenant-modules-grid">
                {(modList.length ? modList : Object.keys(MODULE_LABELS)).map((m) => { const alwaysOn = ["vessels", "pep_panama"].includes(m); const on = alwaysOn || modSel.includes(m); return (
                  <label key={m} className={`flex items-center gap-2 text-xs font-semibold px-2.5 py-2 rounded-lg border cursor-pointer transition-colors ${on ? "border-[#044CBC] bg-blue-50 text-[#044CBC]" : "border-slate-200 text-slate-500"}`} data-testid={`tenant-mod-${m}`}>
                    <input type="checkbox" className="accent-[#044CBC]" checked={on} disabled={alwaysOn}
                           onChange={(e) => setModSel(e.target.checked ? [...modSel, m] : modSel.filter((x) => x !== m))} />
                    {t(MODULE_LABELS[m] || m)}{alwaysOn && <span className="text-[10px] uppercase text-emerald-700">({t("st.module_always")})</span>}
                  </label>
                ); })}
              </div>
            </div>
            <div className="flex flex-wrap gap-3 pt-1">
              <button onClick={saveDetail} disabled={busy} data-testid="tenant-save-button" className="px-4 py-2.5 rounded-lg bg-[#044CBC] text-white text-sm font-bold hover:bg-[#033A8C] disabled:opacity-60 flex items-center gap-2">
                {busy && <Loader2 className="w-4 h-4 animate-spin" />} {t("pf.save_changes")}
              </button>
              {detail.status === "pending" && (
                <>
                  <button onClick={approveFromModal} disabled={busy} data-testid="tenant-approve-button" className="px-4 py-2.5 rounded-lg bg-emerald-600 text-white text-sm font-bold hover:bg-emerald-700 disabled:opacity-60">
                    {t("pf.approve")}
                  </button>
                  <button onClick={() => { setDetail(null); setRejectFor(detail); setReason(""); }} data-testid="tenant-reject-button" className="px-4 py-2.5 rounded-lg bg-red-600 text-white text-sm font-bold hover:bg-red-700">
                    {t("pf.reject")}
                  </button>
                </>
              )}
            </div>
            </>)}
            {ovTab !== "info" && (
              !ov ? (
                <div className="p-8 text-center text-sm text-slate-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />{t("common.loading")}</div>
              ) : (
                <div className="overflow-x-auto rounded-lg border border-slate-200" data-testid={`tenant-ov-${ovTab}`}>
                  <table className="w-full text-sm min-w-[560px]">
                    <thead>
                      <tr className="bg-[#16335F] text-white text-left">
                        {OV_COLS[ovTab].map((c) => <th key={c.key} className="px-3 py-2 text-xs font-bold uppercase tracking-wide">{c.label}</th>)}
                        {ovTab === "forms" && <th className="px-3 py-2 text-xs font-bold uppercase tracking-wide text-right">{t("pf.reassign")}</th>}
                        {ovTab === "users" && <th className="px-3 py-2 text-xs font-bold uppercase tracking-wide text-right" data-testid="pu-actions-header">{t("pu.actions")}</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {(ov[ovTab] || []).length === 0 ? (
                        <tr><td colSpan={OV_COLS[ovTab].length + (ovTab === "forms" || ovTab === "users" ? 1 : 0)} className="px-3 py-6 text-center text-slate-400" data-testid={`tenant-ov-empty-${ovTab}`}>—</td></tr>
                      ) : (ov[ovTab] || []).map((r, i) => (
                        <tr key={i} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`tenant-ov-row-${ovTab}-${i}`}>
                          {OV_COLS[ovTab].map((c) => <td key={c.key} className="px-3 py-2 text-xs text-slate-700">{c.date ? fmtDate(r[c.key]) : c.key === "role" ? t(`u.${r.role}`) : c.key === "active" ? (r.active !== false ? t("u.active") : t("u.inactive")) : (r[c.key] ?? "—")}</td>)}
                          {ovTab === "users" && (
                            <td className="px-3 py-2">
                              <PlatformUserActions user={r} index={i} onChanged={() => api.get(`/platform/tenants/${detail.id}/overview`).then((res) => setOv(res.data))} />
                            </td>
                          )}
                          {ovTab === "forms" && (
                            <td className="px-3 py-2 text-right">
                              <select className="ec-input !py-1 !text-xs" value="" onChange={(e) => e.target.value && reassignForm(r.id, e.target.value)} data-testid={`reassign-form-${i}`}>
                                <option value="">{t("pf.reassign")}</option>
                                {(tenants || []).filter((x) => x.id !== detail?.id && x.active !== false && x.status !== "pending").map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
                              </select>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            )}
          </div>
        </div>
      )}

      {rejectFor && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setRejectFor(null)}>
          <div className="ec-card max-w-md w-full p-6 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="reject-modal">
            <h3 className="font-heading font-bold text-slate-900">{t("pf.reject")} — {rejectFor.name}</h3>
            <div>
              <label className="ec-label">{t("pf.reject_reason")}</label>
              <textarea className="ec-input" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t("pf.reject_optional")} data-testid="reject-reason-input" />
            </div>
            <div className="flex gap-3">
              <button onClick={reject} disabled={busy} data-testid="reject-confirm" className="px-5 py-2.5 rounded-lg bg-red-600 text-white text-sm font-bold hover:bg-red-700 disabled:opacity-60 flex items-center gap-2">
                {busy && <Loader2 className="w-4 h-4 animate-spin" />} {t("pf.reject_confirm")}
              </button>
              <button onClick={() => setRejectFor(null)} data-testid="reject-cancel" className="px-4 py-2.5 rounded-lg border border-slate-200 text-sm font-bold text-slate-600 hover:bg-slate-50">
                {t("u.cancel")}
              </button>
            </div>
          </div>
        </div>
      )}

      {kpiModal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setKpiModal(null)}>
          <div className="ec-card max-w-4xl w-full p-6 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid={`kpi-modal-${kpiModal}`}>
            <div className="flex items-start justify-between gap-3 mb-4">
              <h3 className="font-heading font-bold text-slate-900">{KPI_TITLES[kpiModal]}</h3>
              <button onClick={() => setKpiModal(null)} className="text-slate-400 hover:text-slate-700" data-testid="kpi-modal-close"><X className="w-5 h-5" /></button>
            </div>
            {kpiLoading ? (
              <div className="p-10 text-center text-sm text-slate-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />{t("common.loading")}</div>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-slate-200">
                <table className="w-full text-sm min-w-[640px]">
                  <thead>
                    <tr className="bg-[#16335F] text-white text-left">
                      {KPI_COLUMNS[kpiModal].map((c) => (
                        <th key={c.key} className="px-3 py-2.5 text-xs font-bold uppercase tracking-wide">{c.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {kpiRows.length === 0 ? (
                      <tr><td colSpan={KPI_COLUMNS[kpiModal].length} className="px-3 py-8 text-center text-slate-400" data-testid={`kpi-empty-${kpiModal}`}>—</td></tr>
                    ) : kpiRows.map((r, i) => (
                      <tr key={i} className={`border-t border-slate-100 hover:bg-slate-50 ${(tenants || []).some((x) => x.name === (r.tenant_name || r.name)) ? "cursor-pointer" : ""}`} data-testid={`kpi-row-${kpiModal}-${i}`} onClick={() => { const tnt = (tenants || []).find((x) => x.name === (r.tenant_name || r.name)); if (tnt) { setKpiModal(null); openDetail(tnt); } }}>
                        {KPI_COLUMNS[kpiModal].map((c) => (
                          <td key={c.key} className="px-3 py-2 text-xs text-slate-700">{c.date ? fmtDate(r[c.key]) : (r[c.key] ?? "—")}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="ec-card overflow-hidden" data-testid="tenants-table-card">
        {tenants === null ? (
          <div className="p-10 text-center text-sm text-slate-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />{t("common.loading")}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[820px]">
              <thead>
                <tr className="bg-[#16335F] text-white text-left">
                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-wide">{t("pf.tenants")}</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-wide">RUC</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-wide">{t("pf.users")}</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-wide">{t("pf.searches")}</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-wide">{t("pf.forms")}</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-wide">{t("pf.created")}</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-wide">{t("u.status")}</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-wide text-right">{t("common.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {tenants.map((tnt) => (
                  <tr key={tnt.id} className={`border-t border-slate-100 hover:bg-slate-50 cursor-pointer ${tnt.active === false ? "opacity-50" : ""}`} data-testid={`tenant-row-${tnt.id}`} onClick={() => openDetail(tnt)}>
                    <td className="px-4 py-2.5 font-semibold text-slate-800">
                      {tnt.name}
                      {tnt.id_prefix && <div className="text-[10px] font-mono text-[#044CBC]">{tnt.id_prefix}</div>}
                    </td>
                    <td className="px-4 py-2.5 text-slate-600 text-xs">{tnt.ruc || "—"}</td>
                    <td className="px-4 py-2.5 text-slate-600">{tnt.users_count}</td>
                    <td className="px-4 py-2.5 text-slate-600">{tnt.searches_count}</td>
                    <td className="px-4 py-2.5 text-slate-600">{tnt.forms_count}</td>
                    <td className="px-4 py-2.5 text-slate-500 text-xs">{fmtDate(tnt.created_at)}</td>
                    <td className="px-4 py-2.5">
                      <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${tnt.status === "pending" ? "bg-amber-50 text-amber-700" : tnt.status === "rejected" ? "bg-red-50 text-red-700" : tnt.active !== false ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`} data-testid={`tenant-status-${tnt.id}`}>
                        {tnt.status === "pending" ? t("pf.pending_status") : tnt.status === "rejected" ? t("pf.rejected") : tnt.active !== false ? t("u.active") : t("u.inactive")}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-right whitespace-nowrap">
                      <button onClick={(e) => { e.stopPropagation(); openDetail(tnt); }} data-testid={`tenant-view-${tnt.id}`} className="text-xs font-bold text-[#044CBC] hover:text-[#033A8C] mr-3">
                        {t("pf.view_edit")}
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); toggle(tnt); }} data-testid={`tenant-toggle-${tnt.id}`} className={`text-xs font-bold ${tnt.active !== false ? "text-red-600 hover:text-red-800" : "text-emerald-600 hover:text-emerald-800"}`}>
                        {tnt.active !== false ? t("common.deactivate") : t("common.activate")}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
